import { useState } from 'react'
import type { RuleEditValue } from '@/components/rules/EditRulePropertiesForm'
import { type Rule, RuleLifecycle } from '@/domain/rules.types'
import { useUpdateRule } from '@/hooks/useRules'

/**
 * `<input type="date">` only accepts and emits `YYYY-MM-DD`, while the API stores and
 * returns a full ISO datetime. Without this the control renders blank on an ISO value,
 * so opening the form looks like it cleared the date.
 */
const toDateInput = (iso: string | null): string => (iso ? iso.slice(0, 10) : '')

/**
 * ...and back: the update schema requires a full ISO datetime, so a bare `YYYY-MM-DD`
 * from the date input is rejected with a 422. Midnight UTC keeps the calendar day the
 * user picked (the backend pins TZ=UTC).
 */
const toIsoDateTime = (value: string | null): string | null => {
	if (!value) return null
	return value.length === 10 ? `${value}T00:00:00.000Z` : value
}

/** The rule fields this form owns, with dates in the shape the date inputs need. */
export function toEditValue(rule: Rule): RuleEditValue {
	const {
		statement,
		type,
		severity,
		classification,
		appliesTo,
		checkDefinition,
		validationMethod,
		metadataCheck,
		lifecycle,
		effectiveFrom,
		effectiveTo,
	} = rule
	return {
		statement,
		type,
		severity,
		classification,
		// `appliesTo` is a jsonb column, so a row predating it (or a response whose type
		// over-promises) can arrive without the field. An absent list already means
		// "applies to all", so normalising to [] here loses nothing.
		appliesTo: appliesTo ?? [],
		checkDefinition,
		validationMethod,
		metadataCheck: metadataCheck ?? null,
		lifecycle,
		effectiveFrom: toDateInput(effectiveFrom),
		effectiveTo: toDateInput(effectiveTo) || null,
	}
}

/** Which confirm dialog is open, if any. */
export type PendingAction = 'save' | 'archive' | null

/**
 * The draft form as the update endpoint wants it: date inputs widened to ISO datetimes.
 *
 * A date is sent ONLY when the user actually changed it. The date input can only show a
 * calendar day, so a rule whose `effectiveFrom` carries a time-of-day round-trips as
 * `YYYY-MM-DD` → midnight — sending an untouched field back would silently rewrite a
 * timestamp the user never edited. Comparing against the original day, not the original
 * string, is what makes "unchanged" mean unchanged.
 */
const toUpdatePatch = (form: RuleEditValue, original: Rule) => {
	const { effectiveFrom, effectiveTo, ...rest } = form
	const patch: Partial<Rule> = { ...rest }
	if (effectiveFrom !== toDateInput(original.effectiveFrom)) {
		const iso = toIsoDateTime(effectiveFrom)
		if (iso) patch.effectiveFrom = iso
	}
	if ((effectiveTo || null) !== (toDateInput(original.effectiveTo) || null)) {
		patch.effectiveTo = toIsoDateTime(effectiveTo)
	}
	return patch
}

/**
 * Edit-mode state plus the save/archive mutations for the rule detail page.
 *
 * Extracted from the page so the component stays a render function: it owns the draft
 * form, the confirm-dialog state, and the date conversions the API contract requires.
 */
export function useRuleEditor(rule: Rule) {
	const update = useUpdateRule()
	const [editing, setEditing] = useState(false)
	const [form, setForm] = useState<RuleEditValue | null>(null)
	const [pendingAction, setPendingAction] = useState<PendingAction>(null)

	const startEdit = () => {
		setForm(toEditValue(rule))
		setEditing(true)
	}

	const cancelEdit = () => {
		setEditing(false)
		setForm(null)
	}

	const confirmSave = () => {
		if (!form) return
		update.mutate(
			{ id: rule.id, patch: toUpdatePatch(form, rule) },
			{
				onSuccess: () => {
					setEditing(false)
					setForm(null)
					setPendingAction(null)
				},
				// Close the dialog on failure too, leaving edit mode open with the user's
				// input intact. Without this the dialog sat spinning forever on a rejected
				// save, with nothing said about why.
				onError: () => setPendingAction(null),
			},
		)
	}

	const confirmArchive = () => {
		update.mutate(
			{ id: rule.id, patch: { lifecycle: RuleLifecycle.RETIRED } },
			{ onSuccess: () => setPendingAction(null), onError: () => setPendingAction(null) },
		)
	}

	return {
		editing,
		form,
		setForm,
		pendingAction,
		setPendingAction,
		startEdit,
		cancelEdit,
		confirmSave,
		confirmArchive,
		isSaving: update.isPending,
		saveFailed: update.isError,
	}
}
